import type { Core, EventObject, NodeCollection, NodeSingular } from 'cytoscape';
import { MAPORIUM_GRID_DEFAULTS, MaporiumGridOptions, mergeOptions } from './options';
import { OverlayCanvas, createOverlayCanvas } from './overlay-canvas';
import { drawGrid } from './grid-layer';
import { drawGuides } from './guide-layer';
import {
  AlignmentIndex,
  AlignmentMatch,
  Box,
  Point,
  buildAlignmentIndex,
  gridSnapOffset,
  offsetDelta,
  resolveAlignment,
  shiftBox,
} from './snap-geometry';

const SCRATCH_KEY = '_maporiumGrid';
const BOX_OPTIONS = { includeLabels: false, includeOverlays: false, includeEdges: false };

interface DragSession {
  leaderId: string;
  memberIds: Set<string>;
  nodes: NodeCollection;
  index: AlignmentIndex;
  applied: Point;
}

/** cytoscape's own drag shifts the top-most grabbed nodes and lets the children follow. */
function topMostNodes(nodes: NodeCollection): NodeCollection {
  const ids = new Set<string>();
  nodes.forEach((node) => {
    ids.add(node.id());
  });
  return nodes.filter((node) => node.ancestors().filter((a) => ids.has(a.id())).empty());
}

/** `silentShift` moves the nodes without re-emitting `drag`, and is missing from @types/cytoscape. */
function silentShift(nodes: NodeCollection, delta: Point): void {
  (nodes as unknown as { silentShift: (d: Point) => void }).silentShift(delta);
}

function boxOf(nodes: NodeCollection): Box {
  const bb = nodes.boundingBox(BOX_OPTIONS);
  return { x1: bb.x1, y1: bb.y1, x2: bb.x2, y2: bb.y2 };
}

class MaporiumGridController {
  private options: MaporiumGridOptions = MAPORIUM_GRID_DEFAULTS;
  private gridLayer: OverlayCanvas | null = null;
  private guideLayer: OverlayCanvas | null = null;
  private session: DragSession | null = null;
  private resizeObserver: ResizeObserver | null = null;

  constructor(private readonly cy: Core) {
    cy.on('grab', 'node', this.onGrab);
    cy.on('drag', 'node', this.onDrag);
    cy.on('free', 'node', this.onFree);
    cy.on('pan zoom resize', this.onViewport);
    cy.on('destroy', this.destroy);

    const container = cy.container();
    if (container && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onViewport);
      this.resizeObserver.observe(container);
    }
  }

  setOptions(patch?: Partial<MaporiumGridOptions>): void {
    this.options = mergeOptions(this.options, patch);
    this.syncLayers();
    this.refreshGrid();
  }

  private alignmentEnabled(): boolean {
    return this.options.geometricGuideline || this.options.snapToAlignmentLocationDuringDrag;
  }

  private snappingEnabled(): boolean {
    return this.options.snapToGridDuringDrag || this.options.snapToAlignmentLocationDuringDrag;
  }

  private syncLayers(): void {
    const container = this.cy.container();
    if (!container) {
      return;
    }

    if (this.options.drawGrid && !this.gridLayer) {
      this.gridLayer = createOverlayCanvas(container, this.options.gridStackOrder, 'back');
    } else if (!this.options.drawGrid && this.gridLayer) {
      this.gridLayer.destroy();
      this.gridLayer = null;
    } else if (this.gridLayer) {
      this.gridLayer.setStackOrder(this.options.gridStackOrder);
    }

    if (this.options.geometricGuideline && !this.guideLayer) {
      this.guideLayer = createOverlayCanvas(container, this.options.guidelinesStackOrder);
    } else if (!this.options.geometricGuideline && this.guideLayer) {
      this.guideLayer.destroy();
      this.guideLayer = null;
    } else if (this.guideLayer) {
      this.guideLayer.setStackOrder(this.options.guidelinesStackOrder);
    }
  }

  private refreshGrid(): void {
    if (this.gridLayer) {
      drawGrid(this.cy, this.gridLayer, this.options);
    }
  }

  private readonly onViewport = (): void => {
    this.refreshGrid();
    if (this.session && this.guideLayer) {
      this.resolveFrame(this.session, false);
    }
  };

  private readonly onGrab = (event: EventObject): void => {
    if (!this.snappingEnabled() && !this.options.geometricGuideline) {
      return;
    }

    const target = event.target as NodeSingular;

    // cytoscape emits `grab` once per selected node, all for the same gesture.
    if (this.session?.memberIds.has(target.id())) {
      return;
    }

    const grabbed = (
      target.selected() ? this.cy.nodes(':selected') : target
    ) as unknown as NodeCollection;
    const nodes = topMostNodes(grabbed.nodes());

    if (nodes.empty()) {
      return;
    }

    const memberIds = new Set<string>();
    grabbed.forEach((node) => {
      memberIds.add(node.id());
    });

    const excluded = nodes.union(nodes.ancestors()).union(nodes.descendants());
    const others = this.cy.nodes(':visible').not(excluded);
    const boxes: Box[] = [];
    others.forEach((node) => {
      const bb = node.boundingBox(BOX_OPTIONS);
      boxes.push({ x1: bb.x1, y1: bb.y1, x2: bb.x2, y2: bb.y2 });
    });

    this.session = {
      leaderId: target.id(),
      memberIds,
      nodes,
      index: buildAlignmentIndex(boxes),
      applied: { x: 0, y: 0 },
    };
  };

  private readonly onDrag = (event: EventObject): void => {
    const session = this.session;
    if (!session || (event.target as NodeSingular).id() !== session.leaderId) {
      return;
    }
    this.resolveFrame(session, true);
  };

  private readonly onFree = (): void => {
    this.session = null;
    this.guideLayer?.clear();
  };

  private resolveFrame(session: DragSession, allowShift: boolean): void {
    const zoom = this.cy.zoom();
    const free = shiftBox(boxOf(session.nodes), {
      x: -session.applied.x,
      y: -session.applied.y,
    });

    let matchX: AlignmentMatch | null = null;
    let matchY: AlignmentMatch | null = null;

    if (this.alignmentEnabled()) {
      const tolerance = this.options.guidelinesTolerance / zoom;
      const range = this.options.guidelinesStyle.geometricGuidelineRange;
      matchX = resolveAlignment(free, session.index, 'x', tolerance, range);
      matchY = resolveAlignment(free, session.index, 'y', tolerance, range);
    }

    const desired: Point = { x: 0, y: 0 };

    if (this.options.snapToAlignmentLocationDuringDrag) {
      desired.x = matchX ? matchX.offset : 0;
      desired.y = matchY ? matchY.offset : 0;
    }

    if (this.options.snapToGridDuringDrag) {
      const grid = gridSnapOffset(free, this.options.gridSpacing);
      if (!matchX || !this.options.snapToAlignmentLocationDuringDrag) {
        desired.x = grid.x;
      }
      if (!matchY || !this.options.snapToAlignmentLocationDuringDrag) {
        desired.y = grid.y;
      }
    }

    if (allowShift) {
      const delta = offsetDelta(desired, session.applied);
      if (delta.x !== 0 || delta.y !== 0) {
        silentShift(session.nodes, delta);
      }
      session.applied = desired;
    }

    if (this.guideLayer && this.options.geometricGuideline) {
      drawGuides(this.cy, this.guideLayer, this.options, shiftBox(free, desired), matchX, matchY);
    }
  }

  private readonly destroy = (): void => {
    this.cy.off('grab', 'node', this.onGrab);
    this.cy.off('drag', 'node', this.onDrag);
    this.cy.off('free', 'node', this.onFree);
    this.cy.off('pan zoom resize', this.onViewport);
    this.cy.off('destroy', this.destroy);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.gridLayer?.destroy();
    this.gridLayer = null;
    this.guideLayer?.destroy();
    this.guideLayer = null;
    this.session = null;
    this.cy.scratch(SCRATCH_KEY, undefined);
  };
}

export default function registerMaporiumGrid(cytoscape: unknown): void {
  if (typeof cytoscape !== 'function') {
    return;
  }

  (cytoscape as (type: string, name: string, fn: unknown) => void)(
    'core',
    'maporiumGrid',
    function (this: Core, opts?: Partial<MaporiumGridOptions>) {
      let controller = this.scratch(SCRATCH_KEY) as MaporiumGridController | undefined;

      if (!controller) {
        controller = new MaporiumGridController(this);
        this.scratch(SCRATCH_KEY, controller);
      }

      controller.setOptions(opts);

      return this;
    }
  );
}

export { MAPORIUM_GRID_DEFAULTS };
export type { MaporiumGridOptions, MaporiumGuidelinesStyle } from './options';
